import { useHover, usePress } from '@react-aria/interactions'
import Styles from './EnhancementOption.module.scss'
import { mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ReactNode } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  isSelected: boolean
  onSelect: () => void
  ordinal: number
  title: string
  badgeText?: string
  description?: string[]
  children: ReactNode
  isFromWebsite?: boolean
}

const INTERACTIVE_TAGS = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL', 'ICONBUTTON']

export const EnhancementOption = ({
  isSelected,
  onSelect,
  ordinal,
  title,
  badgeText,
  description,
  children,
  isFromWebsite,
}: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { isPressed, pressProps } = usePress({
    allowTextSelectionOnPress: true, // let inputs/text be focusable
    onPress: (e) => {
      const target = e.target as HTMLElement
      if (INTERACTIVE_TAGS.includes(target.tagName)) return
      onSelect()
    },
  })
  const { isHovered, hoverProps } = useHover({})

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="enhancement-option"
      className={cx(Styles.root, {
        [Styles.selected]: isSelected,
        [Styles.hovered]: isHovered,
        [Styles.pressed]: isPressed,
        [Styles.fromWebsite]: isFromWebsite,
      })}
      {...mergeProps(pressProps, hoverProps)}
    >
      <div className={Styles.header}>
        <ResponsiveDesign.Desktop>
          <div className={cx(Styles.ordinal, { [Styles.selected]: isSelected })}>{ordinal}</div>
          <div className={Styles.title}>
            <Heading level={3} color="var(--dark-80)">
              {title}
            </Heading>
          </div>
          {badgeText && <div className={cx(Styles.badge, { [Styles.selected]: isSelected })}>{badgeText}</div>}
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <div className={Styles.mobileHeaderContainer}>
            <div className={Styles.mobileOrdinalAndBadgeText}>
              <div className={cx(Styles.ordinal, { [Styles.selected]: isSelected })}>{ordinal}</div>
              {badgeText && <div className={cx(Styles.badge, { [Styles.selected]: isSelected })}>{badgeText}</div>}
            </div>
            <div className={Styles.title}>
              <Heading level={isMobile ? 4 : 3} color="var(--dark-80)">
                {title}
              </Heading>
            </div>
          </div>
        </ResponsiveDesign.Mobile>
      </div>
      <div className={Styles.content}>
        {children}
        {description && (
          <div className={Styles.whySection}>
            <Heading level={5} color="var(--dark-80)">
              Here's why:
            </Heading>
            <ul className={Styles.description}>
              {description.map((item, index) => (
                <li key={index}>
                  <Text variant="secondary" color="var(--dark-80)">
                    {item}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
